// ============================================
// Incoming Call Modal Component
// Notification for incoming video calls
// ============================================

import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for playing a ringtone when receiving a call
 * Uses Web Audio API to generate a pleasant ringing sound
 */
const useRingtone = (isRinging) => {
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!isRinging) {
      // Stop all sounds
      oscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {}
      });
      oscillatorsRef.current = [];
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      return;
    }

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
      console.warn('Web Audio API not supported');
      return;
    }

    audioContextRef.current = new AudioContext();
    const ctx = audioContextRef.current;

    // Function to play a melodic ring pattern
    const playRingPattern = () => {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') return;

      const ctx = audioContextRef.current;
      const now = ctx.currentTime;

      // Create a pleasant two-note ringtone (like a phone ring)
      const notes = [
        { freq: 523.25, start: 0, duration: 0.15 },     // C5
        { freq: 659.25, start: 0.15, duration: 0.15 },  // E5
        { freq: 523.25, start: 0.5, duration: 0.15 },   // C5
        { freq: 659.25, start: 0.65, duration: 0.15 },  // E5
      ];

      notes.forEach(note => {
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(note.freq, now + note.start);

        gainNode.gain.setValueAtTime(0, now + note.start);
        gainNode.gain.linearRampToValueAtTime(0.2, now + note.start + 0.02);
        gainNode.gain.setValueAtTime(0.2, now + note.start + note.duration - 0.02);
        gainNode.gain.linearRampToValueAtTime(0, now + note.start + note.duration);

        osc.connect(gainNode);
        gainNode.connect(ctx.destination);

        osc.start(now + note.start);
        osc.stop(now + note.start + note.duration);

        oscillatorsRef.current.push(osc);
      });

      // Clean up oscillators after they're done
      setTimeout(() => {
        oscillatorsRef.current = [];
      }, 1000);
    };

    // Resume audio context and start playing
    ctx.resume().then(() => {
      playRingPattern();
      // Repeat every 2.5 seconds
      intervalRef.current = setInterval(playRingPattern, 2500);
    }).catch(err => {
      console.warn('Could not resume audio context:', err);
    });

    return () => {
      oscillatorsRef.current.forEach(osc => {
        try {
          osc.stop();
        } catch (e) {}
      });
      oscillatorsRef.current = [];
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, [isRinging]);
};

/**
 * Get initials from name
 */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

/**
 * Incoming Call Modal - shows when someone is calling
 */
const IncomingCallModal = ({
  invitation,
  onAccept,
  onDecline,
  onExpire
}) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const [isResponding, setIsResponding] = useState(false);

  // Calculate time left
  useEffect(() => {
    if (!invitation?.expires_at) return;

    const calculateTimeLeft = () => {
      const now = new Date();
      const expires = new Date(invitation.expires_at);
      const diff = Math.max(0, Math.floor((expires - now) / 1000));
      return diff;
    };

    setTimeLeft(calculateTimeLeft());

    const interval = setInterval(() => {
      const remaining = calculateTimeLeft();
      setTimeLeft(remaining);

      if (remaining <= 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [invitation?.expires_at, onExpire]);

  // Handle accept
  const handleAccept = useCallback(async () => {
    if (isResponding) return;
    setIsResponding(true);
    try {
      await onAccept?.(invitation.invitation_id);
    } finally {
      setIsResponding(false);
    }
  }, [invitation?.invitation_id, onAccept, isResponding]);

  // Handle decline
  const handleDecline = useCallback(async () => {
    if (isResponding) return;
    setIsResponding(true);
    try {
      await onDecline?.(invitation.invitation_id);
    } finally {
      setIsResponding(false);
    }
  }, [invitation?.invitation_id, onDecline, isResponding]);

  // Play ringtone when receiving a call
  useRingtone(!!invitation);

  if (!invitation) return null;

  const callerName = invitation.caller_name || 'Unknown Caller';
  const callerType = invitation.caller_type || 'user';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-bounce-slow">
        {/* Caller avatar */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-semibold animate-pulse">
              {getInitials(callerName)}
            </div>
            {/* Ringing animation rings */}
            <div className="absolute inset-0 rounded-full border-4 border-green-500 animate-ping opacity-75"></div>
          </div>

          <h2 className="text-white text-xl font-semibold mt-4">{callerName}</h2>
          <p className="text-gray-400 text-sm capitalize">{callerType}</p>
          <p className="text-green-400 text-sm mt-2 animate-pulse">
            Incoming video call...
          </p>
        </div>

        {/* Timer */}
        <div className="text-center mb-6">
          <p className="text-gray-400 text-sm">
            Call expires in <span className="text-white font-mono">{timeLeft}s</span>
          </p>
          <div className="w-full bg-gray-700 rounded-full h-1 mt-2">
            <div
              className="bg-green-500 h-1 rounded-full transition-all duration-1000"
              style={{ width: `${(timeLeft / 60) * 100}%` }}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-center space-x-6">
          {/* Decline button */}
          <button
            onClick={handleDecline}
            disabled={isResponding}
            className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-50"
          >
            <svg className="w-8 h-8 transform rotate-[135deg]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </button>

          {/* Accept button */}
          <button
            onClick={handleAccept}
            disabled={isResponding}
            className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-500 flex items-center justify-center text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-50"
          >
            {isResponding ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
            ) : (
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            )}
          </button>
        </div>

        {/* Button labels */}
        <div className="flex items-center justify-center space-x-16 mt-3">
          <span className="text-gray-400 text-sm">Decline</span>
          <span className="text-gray-400 text-sm">Accept</span>
        </div>
      </div>

      {/* Custom animation */}
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default IncomingCallModal;
